/**
 * PatientPlanPage (Phase 2.8 Commit 8)
 *
 * Mounted at /org/patient-plans/:planId. Acts as the orchestration hub
 * for an attached PatientPlan — header (payment chip + estimate),
 * plan-level actions (collect upfront, cancel, delete), and the per-step
 * row list with the four flow patterns wired in:
 *
 *   Pattern A — Per-step today: Collect + Start (per_step plan, today's
 *   step, no Sitting yet, has a current visit).
 *   Pattern C — Prepaid: Start (prepaid) for items already covered by
 *   the plan's upfront payment.
 *   B / step scheduling: Convert to appointment for plan-only / dated
 *   steps that haven't been bound to a visit yet (uses existing Phase
 *   2.5 schedule-appointment endpoint).
 *   Edit / Cancel: edit-step (open items), cancel-step (cascade).
 *
 * Day-mode contrast + button-visibility rules from CLAUDE.md applied —
 * solid backgrounds on every secondary button, indigo accents on
 * actionable rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import usePatientPlanStore from '../stores/patientPlanStore';
import { usePermission } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const PAYMENT_MODES = ['UPI', 'CASH', 'CARD', 'CHEQUE', 'ONLINE'];


export default function PatientPlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();

  const planDetail = usePatientPlanStore((s) => s.planDetail);
  const isLoading = usePatientPlanStore((s) => s.isLoadingDetail);
  const error = usePatientPlanStore((s) => s.error);
  const fetchPlanRichDetail = usePatientPlanStore((s) => s.fetchPlanRichDetail);
  const collectUpfrontPayment = usePatientPlanStore((s) => s.collectUpfrontPayment);
  const startAndCollect = usePatientPlanStore((s) => s.startAndCollect);
  const startPrepaid = usePatientPlanStore((s) => s.startPrepaid);
  const editStep = usePatientPlanStore((s) => s.editStep);
  const cancelStep = usePatientPlanStore((s) => s.cancelStep);
  const cancelPlan = usePatientPlanStore((s) => s.cancelPlan);
  const deletePlan = usePatientPlanStore((s) => s.deletePlan);
  const clearPlanDetail = usePatientPlanStore((s) => s.clearPlanDetail);

  const canCancelPlan = usePermission('CANCEL_TREATMENT_PLAN');
  const canCollectPayment = usePermission('COLLECT_PAYMENT');
  const canDraft = usePermission('DRAFT_TREATMENT_PLAN');

  const [actionBusy, setActionBusy] = useState(null);  // step_id or 'plan'
  const [toast, setToast] = useState(null);
  // No payment-mode default — receptionist must pick UPI / CASH / CARD
  // (or CHEQUE / ONLINE) explicitly. Empty blocks collect actions.
  const [collectMode, setCollectMode] = useState('');
  // Phase 2.9 — bulk-select state for the per-step plan flow.
  // Only steps that are open + un-paid + don't yet have a Sitting can be
  // ticked. Submission calls attachPlanItemsToVisit with all selections.
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  const toggleSelected = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  useEffect(() => {
    if (planId) fetchPlanRichDetail(planId);
    return () => clearPlanDetail();
  }, [planId, fetchPlanRichDetail, clearPlanDetail]);

  const plan = planDetail || {};
  const steps = useMemo(() => plan.steps || [], [plan.steps]);
  const today = todayIso();

  const refresh = () => fetchPlanRichDetail(planId);

  const showError = (msg) => setToast({ kind: 'err', text: msg });
  const showOk = (msg) => setToast({ kind: 'ok', text: msg });

  const onCollectUpfront = async () => {
    if (!canCollectPayment) return;
    if (!collectMode) {
      showError('Please select a payment method.');
      return;
    }
    setActionBusy('plan');
    const r = await collectUpfrontPayment(planId, { payment_mode: collectMode });
    setActionBusy(null);
    if (r.success) {
      showOk('Upfront payment collected.');
      refresh();
    } else showError(r.error || 'Failed to collect upfront payment.');
  };

  // Phase 2.9 — bulk collect for per_step plans. Equivalent to clicking
  // Collect+Start on each ticked step. Receptionist can pick a subset;
  // unticked steps stay open.
  const onCollectSelected = async () => {
    if (!canCollectPayment) return;
    if (selectedItemIds.size === 0) return;
    if (!collectMode) {
      showError('Please select a payment method.');
      return;
    }
    const visitId = window.prompt(
      'Visit id for the procedures (default = the visit this plan was attached at):',
      plan.source_visit_id || '',
    );
    if (!visitId || !visitId.trim()) return;
    setActionBusy('plan');
    const itemRefs = Array.from(selectedItemIds).map((iid) => ({
      plan_id: planId,
      item_id: iid,
    }));
    const result = await usePatientPlanStore.getState().attachPlanItemsToVisit(
      visitId.trim(),
      { item_refs: itemRefs, payment_mode: collectMode },
    );
    setActionBusy(null);
    if (result.success) {
      const failed = (result.data?.results || []).filter(
        (r) => r.status === 'error',
      );
      if (failed.length) {
        showError(
          `${failed.length} of ${itemRefs.length} step(s) failed: `
          + failed.map((f) => f.error_message || f.error_code).join('; '),
        );
      } else {
        showOk(`Collected payment + started ${itemRefs.length} step(s).`);
      }
      setSelectedItemIds(new Set());
      refresh();
    } else {
      showError(result.error || 'Bulk collect failed.');
    }
  };

  const onCollectAllPending = () => {
    // "Pay full plan in one go" for per_step plans — selects every
    // collectible step. Phase 2.5 auto-materialises a Sitting at
    // awaiting_payment for any dated step at attach time, so the
    // common today-dated case has hasSitting=true + status=awaiting_payment.
    // attach_plan_items_to_visit is idempotent on those, so we tick
    // them too. Receptionist can untick rows after this for a subset.
    const collectible = (plan.steps || []).filter((s) =>
      !s.prepaid && (
        ((s.status === 'open' || s.status === 'scheduled') && !s.sitting_id)
        || (s.status === 'awaiting_payment' && !!s.sitting_id)
      ),
    );
    setSelectedItemIds(new Set(collectible.map((s) => s.item_id)));
  };

  const onCancelPlan = async () => {
    if (!canCancelPlan) return;
    const reason = window.prompt('Reason for cancelling this plan? (min 3 chars)') || '';
    if (reason.trim().length < 3) return;
    setActionBusy('plan');
    const r = await cancelPlan(planId, { reason });
    setActionBusy(null);
    if (r.success) {
      showOk('Plan cancelled.');
      refresh();
    } else showError(r.error || 'Cancel failed.');
  };

  const onDeletePlan = async () => {
    if (!canCancelPlan) return;
    const ok = window.confirm(
      'Delete this plan permanently? Cancelled rows + sittings will be removed. '
      + 'Refunds (if any) must be recorded offline.',
    );
    if (!ok) return;
    const reason = window.prompt('Reason (min 3 chars)?') || '';
    if (reason.trim().length < 3) return;
    setActionBusy('plan');
    const r = await deletePlan(planId, { reason });
    setActionBusy(null);
    if (r.success) {
      showOk('Plan deleted.');
      navigate(ROUTES.PATIENT_PLANS, { replace: true });
    } else showError(r.error || 'Delete failed.');
  };

  // Per-step actions ----------------------------------------------------

  const onStartAndCollect = async (step) => {
    if (!canCollectPayment) return;
    if (!collectMode) {
      showError('Please select a payment method.');
      return;
    }
    // Phase 2.9 — default to the visit at which the doctor attached
    // the plan. For today's per-step actions the source visit is
    // almost always right; receptionist hits Enter to confirm.
    const visitId = window.prompt(
      'Visit id (default = the visit this plan was attached at):',
      plan.source_visit_id || '',
    );
    if (!visitId || !visitId.trim()) return;
    setActionBusy(step.item_id);
    const r = await startAndCollect(planId, step.item_id, {
      visit_id: visitId.trim(),
      payment_mode: collectMode,
    });
    setActionBusy(null);
    if (r.success) {
      showOk('Step started + payment collected.');
      refresh();
    } else showError(r.error || 'Start + collect failed.');
  };

  const onStartPrepaid = async (step) => {
    const visitId = window.prompt(
      'Visit id (default = the visit this plan was attached at):',
      plan.source_visit_id || '',
    );
    if (!visitId || !visitId.trim()) return;
    setActionBusy(step.item_id);
    const r = await startPrepaid(planId, step.item_id, {
      visit_id: visitId.trim(),
    });
    setActionBusy(null);
    if (r.success) {
      showOk('Step started (prepaid).');
      refresh();
    } else showError(r.error || 'Start failed.');
  };

  const onConvertToAppointment = async (step) => {
    const slotDate = window.prompt(
      'Appointment date (YYYY-MM-DD)?',
      step.scheduled_for ? step.scheduled_for.slice(0, 10) : today,
    );
    if (!slotDate) return;
    const slotTime = window.prompt('Appointment time (HH:MM)?', '10:00');
    if (!slotTime) return;
    setActionBusy(step.item_id);
    const result = await usePatientPlanStore.getState().scheduleAppointmentForItem(
      planId, step.item_id,
      { slot_date: slotDate, slot_time: slotTime },
    );
    setActionBusy(null);
    if (result.success) {
      showOk('Appointment booked.');
      refresh();
    } else showError(result.error || 'Failed to book appointment.');
  };

  const onEditDate = async (step) => {
    const next = window.prompt(
      'New scheduled date (YYYY-MM-DD), blank to clear:',
      step.scheduled_for ? step.scheduled_for.slice(0, 10) : '',
    );
    if (next === null) return;
    setActionBusy(step.item_id);
    const r = await editStep(planId, step.item_id, { scheduled_for: next });
    setActionBusy(null);
    if (r.success) {
      showOk('Step updated.');
      refresh();
    } else showError(r.error || 'Edit failed.');
  };

  const onCancelStep = async (step) => {
    const reason = window.prompt('Reason for cancelling this step? (min 3 chars)') || '';
    if (reason.trim().length < 3) return;
    setActionBusy(step.item_id);
    const r = await cancelStep(planId, step.item_id, { reason });
    setActionBusy(null);
    if (r.success) {
      const msg = r.data?.refund_warning
        ? 'Step cancelled. Refund pending — record offline.'
        : 'Step cancelled.';
      showOk(msg);
      if (r.data?.refund_warning && r.data?.refund_message) {
        setToast({ kind: 'warn', text: r.data.refund_message });
      }
      refresh();
    } else showError(r.error || 'Cancel failed.');
  };

  // Render -------------------------------------------------------------

  const paymentChip = (() => {
    if (plan.payment_mode === 'upfront') {
      if (plan.payment_status === 'upfront_paid') {
        return `Upfront ₹${plan.prepaid_amount || 0} paid`;
      }
      return `Upfront ₹${plan.total_estimate || 0} due`;
    }
    return 'Per visit';
  })();

  const stepIsToday = (s) => {
    if (!s.scheduled_for) return false;
    return String(s.scheduled_for).slice(0, 10) === today;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 p-5">
      <div className="max-w-5xl mx-auto space-y-4">
        <button
          type="button"
          onClick={() => navigate(ROUTES.PATIENT_PLANS)}
          className="text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          ← All patient plans
        </button>

        {/* Header card */}
        <div className="rounded-xl border-2 border-indigo-300 dark:border-indigo-700 bg-white dark:bg-slate-900 p-4">
          {isLoading && !plan.plan_id ? (
            <div className="text-sm text-slate-600 dark:text-slate-300 italic">
              Loading plan…
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-3 mb-2">
                <h1 className="text-lg font-bold text-indigo-900 dark:text-indigo-100">
                  📋 {plan.plan_title || 'Patient plan'}
                </h1>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200">
                  {plan.ordering_rule || 'flexible'}
                </span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
                  {plan.status || 'active'}
                </span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200">
                  {paymentChip}
                </span>
                {plan.source_visit_id && (
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                    title="The visit at which the doctor attached this plan"
                  >
                    visit · {plan.source_visit_id}
                  </span>
                )}
              </div>
              {/* Phase 2.9 — reinforce that this page is the plan's
                  control surface so first-time users know they don't
                  have to chase plan actions across the app. */}
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                Manage this plan end-to-end — collect payment, convert
                steps to appointments, attach to today&apos;s visit.
                Per-procedure receipts also land in{' '}
                <a
                  href={ROUTES.ACTION_PENDING}
                  className="font-semibold text-indigo-700 dark:text-indigo-300 hover:underline"
                >
                  📋 Action Pending →
                </a>.
              </p>
              <div className="text-sm text-slate-700 dark:text-slate-200">
                Collected ₹{Number(plan.collected_to_date || 0).toLocaleString('en-IN')}
                {' / '}
                ₹{Number(plan.total_estimate || 0).toLocaleString('en-IN')}
                {plan.pending_amount != null && Number(plan.pending_amount) > 0 && (
                  <span className="ml-2 text-amber-700 dark:text-amber-300 font-semibold">
                    (Pending ₹{Number(plan.pending_amount).toLocaleString('en-IN')})
                  </span>
                )}
              </div>

              {/* Phase 2.9 — surface cancellation reason + when + by whom
                  whenever the plan was cancelled. Pre-fix this banner
                  was absent and the receptionist had no way to know
                  why a plan disappeared. */}
              {plan.status === 'cancelled' && (
                <div className="mt-3 rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200">
                  <div className="font-bold mb-1">Plan cancelled</div>
                  {plan.cancellation_reason && (
                    <div>
                      <span className="font-semibold">Reason: </span>
                      {plan.cancellation_reason}
                    </div>
                  )}
                  <div className="text-xs mt-1 text-red-700 dark:text-red-300">
                    {plan.cancelled_at && <>at {plan.cancelled_at}</>}
                    {plan.cancelled_by_member_id && (
                      <> · by {plan.cancelled_by_member_id}</>
                    )}
                  </div>
                </div>
              )}

              {/* Plan-level actions */}
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                {plan.payment_mode === 'upfront'
                  && plan.payment_status !== 'upfront_paid'
                  && canCollectPayment && (
                  <>
                    <select
                      value={collectMode}
                      onChange={(e) => setCollectMode(e.target.value)}
                      className="text-xs px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    >
                      <option value="" disabled>Select method…</option>
                      {PAYMENT_MODES.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={onCollectUpfront}
                      disabled={actionBusy === 'plan'}
                      className="text-sm font-semibold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                    >
                      Collect upfront payment
                    </button>
                  </>
                )}
                {/* Phase 2.9 — bulk collect for per_step plans. Equivalent
                    to clicking Collect+Start on each ticked row. The
                    "Pay full plan" shortcut ticks every collectible step
                    in one click; receptionist can untick rows after. */}
                {plan.payment_mode === 'per_step'
                  && plan.status !== 'cancelled'
                  && canCollectPayment && (
                  <>
                    <select
                      value={collectMode}
                      onChange={(e) => setCollectMode(e.target.value)}
                      className="text-xs px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    >
                      <option value="" disabled>Select method…</option>
                      {PAYMENT_MODES.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={onCollectAllPending}
                      disabled={actionBusy === 'plan'}
                      className="text-sm font-medium px-3 py-1.5 rounded-md border border-emerald-400 bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-60"
                    >
                      Select all pending
                    </button>
                    <button
                      type="button"
                      onClick={onCollectSelected}
                      disabled={actionBusy === 'plan' || selectedItemIds.size === 0}
                      className="text-sm font-semibold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                    >
                      Collect &amp; Start ({selectedItemIds.size} selected)
                    </button>
                  </>
                )}
                {canCancelPlan && plan.status !== 'cancelled' && (
                  <button
                    type="button"
                    onClick={onCancelPlan}
                    disabled={actionBusy === 'plan'}
                    className="text-sm font-medium px-3 py-1.5 rounded-md border border-amber-400 bg-white dark:bg-slate-800 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-60"
                  >
                    Cancel plan
                  </button>
                )}
                {canCancelPlan && (
                  <button
                    type="button"
                    onClick={onDeletePlan}
                    disabled={actionBusy === 'plan'}
                    className="text-sm font-medium px-3 py-1.5 rounded-md border border-red-400 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
                  >
                    Delete plan
                  </button>
                )}
                <button
                  type="button"
                  onClick={refresh}
                  className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Refresh
                </button>
              </div>
            </>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={
              'rounded-md p-3 text-sm font-medium '
              + (toast.kind === 'err'
                ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-300'
                : toast.kind === 'warn'
                  ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-300'
                  : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border border-emerald-300')
            }
          >
            <div className="flex items-center justify-between gap-2">
              <span>{toast.text}</span>
              <button
                type="button"
                onClick={() => setToast(null)}
                className="text-xs px-2 py-0.5 rounded bg-white/50 hover:bg-white/80"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Step list */}
        <div className="rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-900 p-4">
          <div className="text-sm font-bold text-indigo-900 dark:text-indigo-100 mb-2">
            Steps ({steps.length})
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">
              {error}
            </div>
          )}
          {!isLoading && steps.length === 0 && (
            <div className="text-sm text-slate-600 dark:text-slate-300 italic">
              No steps in this plan.
            </div>
          )}
          <div className="space-y-2">
            {steps.map((s) => {
              const isToday = stepIsToday(s);
              const hasSitting = !!s.sitting_id;
              const isOpen = s.status === 'open' || s.status === 'scheduled';
              const isPrepaid = !!s.prepaid;
              const isCancelled = s.status === 'cancelled';
              const isPerStep = plan.payment_mode === 'per_step';
              const busy = actionBusy === s.item_id;
              // Phase 2.9 hotfix — eligible for bulk-collect ticking
              // either when the step has no sitting yet (will be created
              // by attach_plan_items_to_visit), OR when a sitting is
              // already at awaiting_payment (Phase 2.5 auto-materialises
              // these for any dated step; the bulk handler is idempotent
              // and just collects payment on the existing sitting).
              const isCollectible = isPerStep && !isPrepaid && !isCancelled && (
                (isOpen && !hasSitting)
                || (hasSitting && s.status === 'awaiting_payment')
              );
              const isSelected = selectedItemIds.has(s.item_id);

              return (
                <div
                  key={s.item_id}
                  className={
                    'p-3 rounded-md border-2 space-y-2 '
                    + (isCancelled
                      ? 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/40 opacity-70'
                      : isSelected
                        ? 'border-emerald-500 dark:border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30'
                        : 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30')
                  }
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    {isCollectible && canCollectPayment && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelected(s.item_id)}
                        className="h-4 w-4"
                        title="Tick to include this step in the bulk Collect & Start"
                      />
                    )}
                    <span className="text-sm font-bold text-indigo-900 dark:text-indigo-100">
                      {s.step_number || '·'}. {s.procedure_name || s.procedure_id}
                    </span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100">
                      {s.status}
                    </span>
                    {s.is_mandatory && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-200 dark:bg-indigo-800 text-indigo-900 dark:text-indigo-100">
                        Mandatory
                      </span>
                    )}
                    {s.scheduled_for && (
                      <span className="text-xs text-slate-700 dark:text-slate-200">
                        📅 {String(s.scheduled_for).slice(0, 10)}
                      </span>
                    )}
                    {s.assignee_name && (
                      <span className="text-xs text-slate-700 dark:text-slate-200">
                        👤 {s.assignee_name}
                      </span>
                    )}
                    {/* Phase 2.9 — visible breadcrumb when this step has
                        been converted to an appointment. The detail
                        projection already returns appointment_id. */}
                    {s.appointment_id && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-200 dark:bg-violet-800 text-violet-900 dark:text-violet-100"
                        title={`Linked appointment ${s.appointment_id}`}
                      >
                        🗓️ Appointment
                      </span>
                    )}
                    <span className="text-xs font-semibold ml-auto text-indigo-900 dark:text-indigo-100">
                      {s.payment_chip}
                    </span>
                  </div>

                  {!isCancelled && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {/* Pattern A — Collect + Start (per-step today). */}
                      {isPerStep && isOpen && !hasSitting && isToday && canCollectPayment && (
                        <>
                          <select
                            value={collectMode}
                            onChange={(e) => setCollectMode(e.target.value)}
                            disabled={busy}
                            className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          >
                            <option value="" disabled>Select method…</option>
                            {PAYMENT_MODES.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => onStartAndCollect(s)}
                            disabled={busy}
                            className="text-xs font-semibold px-3 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                          >
                            Collect + Start
                          </button>
                        </>
                      )}
                      {/* Pattern C — Start (prepaid). */}
                      {isPrepaid && isOpen && !hasSitting && isToday && (
                        <button
                          type="button"
                          onClick={() => onStartPrepaid(s)}
                          disabled={busy}
                          className="text-xs font-semibold px-3 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                        >
                          Start (prepaid)
                        </button>
                      )}
                      {/* Convert to appointment — needs an existing
                          (dated, sitting-materialised) item per Phase
                          2.5 schedule-appointment. */}
                      {hasSitting && !s.appointment_id && (
                        <button
                          type="button"
                          onClick={() => onConvertToAppointment(s)}
                          disabled={busy}
                          className="text-xs font-medium px-3 py-1 rounded-md border border-indigo-400 bg-white dark:bg-slate-800 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-60"
                        >
                          Convert to appointment
                        </button>
                      )}
                      {/* Edit date — open-items-only (no sitting). */}
                      {isOpen && !hasSitting && canDraft && (
                        <button
                          type="button"
                          onClick={() => onEditDate(s)}
                          disabled={busy}
                          className="text-xs font-medium px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
                        >
                          Edit date
                        </button>
                      )}
                      {/* Cancel step. */}
                      {!isCancelled && canCancelPlan && (
                        <button
                          type="button"
                          onClick={() => onCancelStep(s)}
                          disabled={busy}
                          className="text-xs font-medium px-3 py-1 rounded-md border border-red-400 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
                        >
                          Cancel step
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
